import _ from "underscore";

import { Level1Id as MasterformatLevel1, Level2Id as MasterformatLevel2, Level3Id as MasterformatLevel3, Level4Id as MasterformatLevel4, Level5Id as MasterformatLevel5 } from "masterformat";
import reduceElementStatusFilter, { defaultElementStatusFilter, ElementStatusFilterEvents, ElementStatusFilterStore } from "./reduce_element_status_filter";
import reduceTradesFilter, { TradesFilterEvents, TradesFilterStore } from "./reduce_trades_filter";
import reduceUniformatFilter, { defaultUniformatFilter, UniformatFilterEvents, UniformatFilterStore } from "./reduce_uniformat_filter";
import { ANNOTATION_TITLE_FILTER_CHANGED, AnnotationTitleFilterChangedEvent } from "../../../events/viewer/annotation_title_filter_changed";
import { DEVIATIONS_FROM_ALL_SCANS_SHOWN, DeviationsFromAllScansShownEvent } from "../../../events/viewer/deviations_from_all_scans_shown";
import { INSPECTION_MODE_SELECTED, InspectionModeSelectedEvent } from "../../../events/selection/inspection_mode_selected";
import { InspectionMode } from "../../../models/domain/enums/inspection_modes";
import { ISSUE_STATUS_FILTER_CHANGED, IssueStatusFilterChangedEvent } from "../../../events/viewer/issue_status_filter_changed";
import { Level1Id, Level2Id, Level3Id, Level4Id, UniformatId } from "uniformat";
import { MASTERFOMAT_FILTER_LOADED, MasterformatFilterLoadedEvent } from "../../../events/loaded/masterformat_filter_loaded";
import { MASTERFORMAT_FILTER_UPDATED, MasterformatFilterUpdatedEvent } from "../../../events/selection/work_breakdown_structure/masterformat_filter_updated";
import { MasterformatId } from "masterformat";
import { ONLY_DEVIATIONS_FROM_CURRENT_SCAN_SHOWN, OnlyDeviationsFromCurrentScanShownEvent } from "../../../events/viewer/only_deviations_from_current_scan_shown";
import { REVIEW_MODE_TOGGLED_OFF, ReviewModeToggledOffEvent } from "../../../events/viewer/review_mode_toggled_off";
import { SHOW_UNCLASSIFIED_ELEMENTS_UPDATED, ShowUnclassifiedElementsUpdatedEvent } from "../../../events/show_unclassified_elements_updated";
import { TOLERANCE_FILTER_CHANGED, ToleranceFilterChangedEvent } from "../../../events/viewer/tolerance_filter_changed";
import { VIEW_LOADED, ViewLoadedEvent } from "../../../events/loaded/view_loaded";
import { TRADE_HIDDEN, TradeHiddenEvent } from "../../../events/selection/trades/trade_hidden";
import { TRADE_HIDDEN_ALL, TradeHiddenAllEvent } from "../../../events/selection/trades/trade_hidden_all";
import { TRADE_SHOWN, TradeShownEvent } from "../../../events/selection/trades/trade_shown";
import { TRADE_SHOWN_ALL, TradeShownAllEvent } from "../../../events/selection/trades/trade_shown_all";

import { defaultMasterformatFilter } from "./default_masterformat_filter";

import type { Reducer } from "redux";
import type { Selected } from "type_aliases";
import MasterformatProgressConverter from "../../converters/masterformat_progress_converter";

export type Level1Filter = { [code in Level1Id]: Selected } | Record<string, never>;
export type Level2Filter = { [code in Level2Id]: Selected } | Record<string, never>;
export type Level3Filter = { [code in Level3Id]: Selected } | Record<string, never>;
export type Level4Filter = { [code in Level4Id]: Selected } | Record<string, never>;
export type Level5Filter = { [code: string]: Selected } | Record<string, never>;
type Filters = Level1Filter | Level2Filter | Level3Filter | Level4Filter | Level5Filter;

export type MasterFormatClassLevel1Filter = { [code in MasterformatLevel1]: Selected };
export type MasterFormatClassLevel2Filter = { [code in MasterformatLevel2]: Selected };
export type MasterFormatClassLevel3Filter = { [code in MasterformatLevel3]: Selected };
export type MasterFormatClassLevel4Filter = { [code in MasterformatLevel4]: Selected };
export type MasterFormatClassLevel5Filter = { [code in MasterformatLevel5]: Selected };
export type MasterFormatUnclassifiedFilter = { selected?: 1 | 0 };

export type FilterType = "masterformat" | "uniformat";
type FormatId<T extends FilterType> = T extends "masterformat" ? MasterformatId : UniformatId;

export type FormatCodeTreeNode = {
  visited: boolean,
  value: Selected,
  children: FormatCodeTreeNode[],
  code: FormatId<any>
};

export type MasterformatFilter = {
  level1: MasterFormatClassLevel1Filter,
  level2: MasterFormatClassLevel2Filter,
  level3: MasterFormatClassLevel3Filter,
  level4: MasterFormatClassLevel4Filter,
  level5?: MasterFormatClassLevel5Filter,
  unclassified?: MasterFormatUnclassifiedFilter
}

export enum IssueStatusFilter {
  NONE = "NONE",
  UNEXPORTED = "UNEXPORTED",
  EXPORTED = "EXPORTED",
  EXPORTED_BIMTRACK_ONLY = "EXPORTED_BIMTRACK_ONLY",
  EXPORTED_BCF_ONLY = "EXPORTED_BCF_ONLY",
  EXPORTED_PROCORE_ONLY = "EXPORTED_PROCORE_ONLY",
  EXPORTED_ACC_ONLY = "EXPORTED_ACC_ONLY",
  EXPORTED_REVIZTO_ONLY = "EXPORTED_REVIZTO_ONLY"
}

export interface FilterStore {
  toleranceFilter: number;
  issueStatusFilter: IssueStatusFilter;
  uniformat: UniformatFilterStore;
  onlyShowDeviationsFromCurrentScan: boolean;
  elementStatuses: ElementStatusFilterStore;
  masterFormats: MasterformatFilter;
  trades: TradesFilterStore;
  annotationTitleFilter: string;
  showUnclassifiedElements: boolean;
}

export const defaultFilter: FilterStore = {
  uniformat: defaultUniformatFilter,
  toleranceFilter: 0.0762,
  issueStatusFilter: IssueStatusFilter.NONE,
  onlyShowDeviationsFromCurrentScan: false, // this does not affect anything, see store_in_local_storage.ts
  elementStatuses: defaultElementStatusFilter,
  annotationTitleFilter: "",
  masterFormats: defaultMasterformatFilter,
  trades: {
    unclassified: {
      id: "unclassified",
      selected: 1,
      parentId: null,
      childIds: [],
      workPackageId: null
    }
  },
  showUnclassifiedElements: true,
};

type FilterEvents =
  | IssueStatusFilterChangedEvent
  | DeviationsFromAllScansShownEvent
  | MasterformatFilterUpdatedEvent
  | OnlyDeviationsFromCurrentScanShownEvent
  | ToleranceFilterChangedEvent
  | ViewLoadedEvent
  | AnnotationTitleFilterChangedEvent
  | ShowUnclassifiedElementsUpdatedEvent
  | MasterformatFilterLoadedEvent
  | InspectionModeSelectedEvent
  | ReviewModeToggledOffEvent
  | UniformatFilterEvents
  | TradesFilterEvents
  | ElementStatusFilterEvents
  | TradeHiddenAllEvent
  | TradeHiddenEvent
  | TradeShownAllEvent
  | TradeShownEvent;

const reduceFilter: Reducer<FilterStore, FilterEvents> = (filter = defaultFilter, event?) => {
  const trades = reduceTradesFilter(filter.trades, event as TradesFilterEvents);
  const uniformat = reduceUniformatFilter(filter.uniformat, event as UniformatFilterEvents);
  const elementStatuses = reduceElementStatusFilter(filter.elementStatuses, event as ElementStatusFilterEvents);
  if (trades !== filter.trades) {
    filter = {
      ...filter,
      trades
    };
  }
  if (uniformat !== filter.uniformat) {
    filter = {
      ...filter,
      uniformat
    };
  }
  if (elementStatuses !== filter.elementStatuses) {
    filter = {
      ...filter,
      elementStatuses
    };
  }
  switch (event?.type) {
    case VIEW_LOADED: {
      const { deviationTolerance, onlyShowDeviationsFromCurrentScan } = event.payload.viewAttributes.filters;
      return {
        ...filter,
        toleranceFilter: deviationTolerance.value,
        ...(onlyShowDeviationsFromCurrentScan != null ? { onlyShowDeviationsFromCurrentScan } : {})
      };
    }
    case MASTERFORMAT_FILTER_UPDATED: {
      return {
        ...filter,
        masterFormats: {
          ...event.payload.masterformatFilter
        }
      };
    }
    case TOLERANCE_FILTER_CHANGED: {
      return {
        ...filter,
        toleranceFilter: event.payload
      };
    }
    case ISSUE_STATUS_FILTER_CHANGED: {
      return {
        ...filter,
        issueStatusFilter: event.payload
      };
    }
    case ONLY_DEVIATIONS_FROM_CURRENT_SCAN_SHOWN: {
      return {
        ...filter,
        onlyShowDeviationsFromCurrentScan: true
      };
    }
    case DEVIATIONS_FROM_ALL_SCANS_SHOWN: {
      return {
        ...filter,
        onlyShowDeviationsFromCurrentScan: false
      };
    }
    case REVIEW_MODE_TOGGLED_OFF: {
      return {
        ...filter,
        verifiedElementsShown: true
      };
    }
    case ANNOTATION_TITLE_FILTER_CHANGED: {
      return {
        ...filter,
        annotationTitleFilter: event.payload
      };
    }
    case SHOW_UNCLASSIFIED_ELEMENTS_UPDATED: {
      return {
        ...filter,
        showUnclassifiedElements: event.payload
      };
    }
    case TRADE_SHOWN: {
      return {
        ...filter,
        // @ts-ignore
        masterFormats: MasterformatProgressConverter.toggleMasterformatTrade([event.payload.code], 1, filter.masterFormats)
      }
    }
    case TRADE_HIDDEN: {
      return {
        ...filter,
        // @ts-ignore
        masterFormats: MasterformatProgressConverter.toggleMasterformatTrade([event.payload.code], 0, filter.masterFormats)
      }
    }
    case TRADE_HIDDEN_ALL: {
      return {
        ...filter,
        // @ts-ignore
        masterFormats: MasterformatProgressConverter.toggleAllMasterformats(0, filter.masterFormats)
      }
    }
    case TRADE_SHOWN_ALL: {
      return {
        ...filter,
        // @ts-ignore
        masterFormats: MasterformatProgressConverter.toggleAllMasterformats(1, filter.masterFormats)
      }
    }
    case MASTERFOMAT_FILTER_LOADED: {
      return {
        ...filter,
        masterFormats: {
          ...event.payload
        }
      };
    }
    case INSPECTION_MODE_SELECTED: {
      const onlyShowDeviationsFromCurrentScan = event.payload === InspectionMode.inspect;
      return {
        ...filter,
        onlyShowDeviationsFromCurrentScan,
      };
    }
    default: {
      return filter;
    }
  }
};

export default reduceFilter;

export const toggleParents = (node: FormatCodeTreeNode, type?: FilterType) => {
  if (node.children.length === 0) {
    node.visited = true;
  } else if (node.visited === false) {
    if (canComputeValue(node)) {
      node.value = type === "masterformat" ? computeMasterformatValue(node) : computeValue(node);
      node.visited = true;
    } else {
      node.children.forEach(child => toggleParents(child, type));
    }
    node.value = type === "masterformat" ? computeMasterformatValue(node) : computeValue(node);
    node.visited = true;
  }
};

const canComputeValue = (treeNode: FormatCodeTreeNode): boolean => {
  return _.every(treeNode.children, (node) => node.visited === true);
};

const computeValue = (treeNode: FormatCodeTreeNode): Selected => {
  if (_.every(treeNode.children, (node => node.value === 1))) {
    return 1;
  } else if (_.every(treeNode.children, (node => node.value === 0))) {
    return 0;
  } else {
    return -1;
  }
};

const computeMasterformatValue = (treeNode: FormatCodeTreeNode): Selected => {
  if (_.every(treeNode.children, (node => node.value === 1))) {
    return 1;
  } else if (_.every(treeNode.children, (node => node.value === 0))) {
    return 0;
  } else {
    return -1;
  }
};

export function toggleChildTrades<Trade extends Filters | MasterformatFilter>(code: UniformatId | MasterformatId, newValue: Selected, trades: Trade): Trade {
  return _.mapObject(trades, (originalValue, childCode: UniformatId | MasterformatId) => {
    if ((childCode as string).startsWith(code)) {
      return newValue;
    } else {
      return originalValue;
    }
  }) as Trade;
}
